import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';
import { WaitlistEntry } from '@dreryk/egabinet-waitlist-frontend-api';

import { TimetableSlotSelectionEvent } from '@dreryk/react-calendar';
import { ButtonVariant, dispatchSnackbarPositiveEvent, ModalConfirmButton } from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';

import { appointmentsTranslationKeys, waitlistEntryTranslationKeys } from '../../../../translations';
import { AppointmentWaitlistCreateActionEventInterceptor } from '../interceptors/AppointmentWaitlistCreateActionEventInterceptor';

import { AppointmentWaitlistCreateModal } from './AppointmentWaitlistCreateModal';
import { AppointmentWaitlistCreateBookAction } from './AppointmentWaitlistCreateBookAction';

type AppointmentWaitlistPatientBookModalProps = {
   readonly selectedSlot: TimetableSlotSelectionEvent<AppointmentPractitioner>;
   readonly onClose: VoidFunction;
   readonly onAppointmentCreated: VoidFunction;
   readonly waitlistEntry: WaitlistEntry;
};

export const AppointmentWaitlistPatientBookModal = (props: AppointmentWaitlistPatientBookModalProps): JSX.Element => {
   const { onAppointmentCreated } = props;
   const history = useHistory();
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const { waitlistEntryId, bookPatientId, ...otherSearchParams } = visitPaths.useCalendarSearchParams();
   const handleBookActionSuccess = useCallback((dateConfirmedWithPatient: boolean) => {
      dispatchSnackbarPositiveEvent(
         dateConfirmedWithPatient
            ? waitlistEntryTranslationKeys.successfullyCreatedNewAppointment
            : waitlistEntryTranslationKeys.successfullyCreatedTemporaryAppointment
      );
   }, []);
   const handleCreateActionSuccess = useCallback((dateConfirmedWithPatient: boolean) => {
      dispatchSnackbarPositiveEvent(
         dateConfirmedWithPatient
            ? waitlistEntryTranslationKeys.successfullyCreatedNewAppointmentAndChangedEntryStatus
            : waitlistEntryTranslationKeys.successfullyCreatedTemporaryAppointmentAndChangedEntryStatus
      );
   }, []);
   const handleCreateAction = useCallback(() => {
      onAppointmentCreated();
      history.replace(visitPaths.getCalendarPath({ searchParams: otherSearchParams }));
   }, [history, onAppointmentCreated, otherSearchParams]);
   return (
      <AppointmentWaitlistCreateModal
         selectedSlot={props.selectedSlot}
         onClose={props.onClose}
         waitlistEntry={props.waitlistEntry}
         urgent={props.waitlistEntry.importance === 'IMPORTANT'}
         confirmButton={({ handleSubmit, setAppointmentTimeSlotOccupiedError }) => (
            <>
               <AppointmentWaitlistCreateActionEventInterceptor
                  handleSubmit={handleSubmit}
                  onAppointmentTimeSlotOccupiedFault={setAppointmentTimeSlotOccupiedError}
                  waitlistEntryId={props.waitlistEntry.id}
                  onSuccess={handleCreateActionSuccess}
               >
                  <ModalConfirmButton
                     label={appointmentsTranslationKeys.create.modal.confirm.register}
                     onClick={handleCreateAction}
                     variant={ButtonVariant.SECONDARY}
                     testId={'createAppointment'}
                  />
               </AppointmentWaitlistCreateActionEventInterceptor>
               <AppointmentWaitlistCreateBookAction
                  handleSubmit={handleSubmit}
                  onAppointmentTimeSlotOccupiedFault={setAppointmentTimeSlotOccupiedError}
                  waitlistEntryId={props.waitlistEntry.id}
                  onSuccess={handleBookActionSuccess}
               >
                  {({ label }) => (
                     <ModalConfirmButton
                        label={label}
                        onClick={props.onAppointmentCreated}
                        testId={'createAppointmentAndGoBack'}
                     />
                  )}
               </AppointmentWaitlistCreateBookAction>
            </>
         )}
      />
   );
};
